<script lang="ts" setup>
import OverlayBase from './OverlayBase.vue'
import SkrButton from '@/components/button/SkrButton.vue'
import IllustrationSigningFailure from '@/components/identification/IllustrationSigningFailure.vue'

const props = defineProps<{
  signatureRequestId: string
  signatureId: string
}>()

const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()

const { invitingBusinessId } = storeToRefs(useDirectSignStore())
const { rootStyle, publicBusinessInfo, isThemeReady } = useIdentificationTheme(invitingBusinessId)
const { logError } = useErrorHandling()
const mabStore = useMabStore()
const exitUrlStore = useExitUrlStore()
const { errorUrl } = storeToRefs(exitUrlStore)

const transactionId = ref('')
const isTransactionIdSettled = ref(false)

const showSkribbleSupport = computed(
  () => isThemeReady.value && !publicBusinessInfo.value?.mabSettings?.skribbleSupport?.disabled
)
// Wait for everything the overlay shows, so the support block never pops in after the rest.
const isLoading = computed(() => !isThemeReady.value || (showSkribbleSupport.value && !isTransactionIdSettled.value))

const onContinue = () => {
  if (errorUrl.value && exitUrlStore.areExitUrlsApplicable([props.signatureRequestId])) {
    exitUrlStore.leaveToErrorUrl()
    return
  }
  emit('close')
}
const contactSupportLink = computed(() => {
  const supportLink = t('sign.error.xqes_overlay.contact_support_link')
  return transactionId.value ? `${supportLink}?identtransactionid=${transactionId.value}` : supportLink
})

const fetchTransactionId = async () => {
  try {
    const { transactionId: fetchedTransactionId } = await mabStore.getMabIdentSessionInfo(
      props.signatureRequestId,
      props.signatureId
    )
    transactionId.value = fetchedTransactionId
  } catch (error) {
    logError('Failed to fetch MAB ident session info', error)
  } finally {
    isTransactionIdSettled.value = true
  }
}

watch(
  showSkribbleSupport,
  show => {
    if (show && !isTransactionIdSettled.value) void fetchTransactionId()
  },
  { immediate: true }
)
</script>

<template>
  <overlay-base :show-header="false">
    <div class="identification-typography" :style="rootStyle">
      <skr-spinner v-if="isLoading" fullscreen data-cy="xqes_failure_loading" />
      <section v-else class="sign-failed" data-cy="overlay_signing_failure_xqes">
        <illustration-signing-failure class="sign-failed__illustration" />
        <skr-heading level="1" class="sign-failed__title">{{ t('sign.error.xqes_overlay.title') }}</skr-heading>
        <div class="sign-failed__divider"></div>
        <p class="sign-failed__message">
          {{ t('sign.error.xqes_overlay.failure_reason_ident') }}
        </p>
        <template v-if="showSkribbleSupport && transactionId">
          <div class="sign-failed__transaction">
            <span class="sign-failed__transaction-label">{{ t('sign.error.xqes_overlay.transaction_id') }}</span>
            <span class="sign-failed__transaction-id" data-cy="xqes_failure_transaction_id">{{ transactionId }}</span>
          </div>
          <p class="sign-failed__support">
            {{ t('sign.error.xqes_overlay.need_help') }}
            <a target="_blank" rel="noopener" :href="contactSupportLink" data-cy="xqes_failure_contact_support">
              {{ t('sign.error.xqes_overlay.contact_support') }}
            </a>
          </p>
        </template>
        <div class="sign-failed__divider"></div>
        <skr-button
          type="secondary"
          color="primary-xqes"
          size="xl"
          data-cy="xqes_failure_continue_button"
          class="sign-failed__continue-btn"
          @click="onContinue"
        >
          {{ t('sign.error.xqes_overlay.continue') }}
        </skr-button>
      </section>
    </div>
  </overlay-base>
</template>

<style lang="scss" scoped>
.sign-failed {
  min-height: 100dvh;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: safe center;
  gap: 6 * $bw;
  padding: 6 * $bw 8 * $bw 8 * $bw;
  text-align: center;
  color: rgb(var(--v-theme-on-surface-high-xqes));

  &__title {
    max-width: 328px;
    padding-bottom: 3 * $bw;
    font-weight: 700;
  }

  &__divider {
    width: 100%;
    height: 1px;
    background-color: rgb(var(--v-theme-on-surface-low-xqes));
  }

  &__message {
    font-size: 1.125rem;
    line-height: 1.6875rem;
    font-weight: 700;
  }

  &__transaction {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: $bw;
    width: 306px;
    padding: 2 * $bw;
    border-radius: 4px;
    background-color: rgb(var(--v-theme-surface-xqes));
  }

  &__transaction-label {
    font-size: 0.875rem;
    line-height: normal;
    color: rgb(var(--v-theme-on-surface-xqes));
  }

  &__transaction-id {
    font-size: 1rem;
    line-height: 1.4375rem;
    color: rgb(var(--v-theme-on-surface-high-xqes));
  }

  &__support {
    font-size: 1rem;
    line-height: 1.625rem;

    a {
      color: rgb(var(--v-theme-primary-xqes));
      text-decoration: none;
    }
  }

  &__continue-btn {
    margin-top: 2 * $bw;
  }
}
</style>
